import React from 'react';
import { useIntl } from 'react-intl';
import {
  COLORS,
  STATUS_STRIPE,
  TILE_BOOKING_BG,
  TILE_TRIP_BG,
} from '../constants';

// Floating colour key for the timeline. Entries with a `category` double as FILTER
// toggles — click one to hide/show that kind of element on the canvas. The swatch
// goes hollow + the label is muted/struck-through when off, so the toggle reads
// inside the legend's own visual language (no separate checkbox chrome). Pinned
// bottom-RIGHT over the day area (never over the sticky left columns).

export type LegendCategory = 'booked' | 'rental' | 'releaseReady' | 'blocked';
export type LegendFilters = Record<LegendCategory, boolean>;

type Props = {
  // Sticky-left width (plate + extra columns) — caps the banner so it never reaches
  // the sticky left columns.
  leftWidth: number;
  filters: LegendFilters;
  onToggle: (category: LegendCategory) => void;
};

const SWATCH = 16;

const swatchBase: React.CSSProperties = {
  width: SWATCH,
  height: SWATCH,
  borderRadius: 4,
  flex: '0 0 auto',
  border: '1px solid rgba(15, 23, 42, 0.18)',
  boxSizing: 'border-box',
};

type Swatch =
  | { kind: 'solid'; color: string }
  | { kind: 'striped'; c1: string; c2: string };

// On → filled/striped swatch; off → hollow box (just the border).
const swatchStyle = (swatch: Swatch, on: boolean): React.CSSProperties => {
  if (swatch.kind === 'solid') {
    return { ...swatchBase, background: on ? swatch.color : 'transparent' };
  }
  return {
    ...swatchBase,
    // 45deg → "\" diagonal, matching the canvas candy-stripe.
    backgroundImage: on
      ? `repeating-linear-gradient(45deg, ${swatch.c1} 0, ${swatch.c1} 5px, ${swatch.c2} 5px, ${swatch.c2} 10px)`
      : 'none',
  };
};

// Some status keys resolve to lowercase values (e.g. de "geblockt"); capitalize.
const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// Neutral light grey representing RELEASED — those cells are intentionally un-banded;
// it's an informational key (nothing is drawn for it), so it is NOT a toggle.
const RELEASED_NEUTRAL = '#E5E7EB';

type LegendItem = { id: string; category?: LegendCategory; swatch: Swatch };

const ITEMS: LegendItem[] = [
  {
    id: 'bookings.headline.booked',
    category: 'booked',
    swatch: { kind: 'solid', color: TILE_BOOKING_BG },
  },
  // Lime = active rentals / finished trips → reuse the RENTED car-status key so the
  // legend matches the group-by-status badge ("gemietet").
  {
    id: 'fleetadmin.carStatus.RENTED',
    category: 'rental',
    swatch: { kind: 'solid', color: TILE_TRIP_BG },
  },
  {
    id: 'fleetadmin.carStatus.RELEASED',
    swatch: { kind: 'solid', color: RELEASED_NEUTRAL },
  },
  {
    id: 'fleetadmin.carStatus.RELEASE_READY',
    category: 'releaseReady',
    swatch: {
      kind: 'striped',
      c1: STATUS_STRIPE.releaseReady.c1,
      c2: STATUS_STRIPE.releaseReady.c2,
    },
  },
  {
    id: 'fleetadmin.carStatus.BLOCKED',
    category: 'blocked',
    swatch: {
      kind: 'striped',
      c1: STATUS_STRIPE.blocked.c1,
      c2: STATUS_STRIPE.blocked.c2,
    },
  },
];

const ColorLegend: React.FC<Props> = ({ leftWidth, filters, onToggle }) => {
  const intl = useIntl();
  return (
    <div
      aria-label="Timeline colour legend"
      style={{
        position: 'absolute',
        bottom: 16,
        // Offset from the right edge to clear the Intercom chat bubble in the corner.
        right: 84,
        zIndex: 5,
        // Hard cap so the banner can never reach the sticky left columns.
        maxWidth: `calc(100% - ${leftWidth + 100}px)`,
        // The container is click-through; only the toggle items below capture clicks,
        // so clicking/dragging the timeline elsewhere still works.
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 14,
        padding: '8px 12px',
        background: 'rgba(255, 255, 255, 0.96)',
        border: `1px solid ${COLORS.gridBorder}`,
        borderRadius: 10,
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.14)',
        fontSize: 12,
        lineHeight: 1,
        color: '#334155',
      }}
    >
      {ITEMS.map((item) => {
        const toggleable = !!item.category;
        const on = item.category ? filters[item.category] : true;
        const label = capitalize(intl.formatMessage({ id: item.id }));
        return (
          <div
            key={item.id}
            role={toggleable ? 'button' : undefined}
            aria-pressed={toggleable ? on : undefined}
            onClick={toggleable ? () => onToggle(item.category!) : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              // Only toggle items capture clicks; the rest stay click-through.
              pointerEvents: toggleable ? 'auto' : 'none',
              cursor: toggleable ? 'pointer' : 'default',
              userSelect: 'none',
              opacity: on ? 1 : 0.9,
              transition: 'opacity 120ms ease',
            }}
          >
            <span style={swatchStyle(item.swatch, on)} />
            <span
              style={{
                whiteSpace: 'nowrap',
                color: on ? '#334155' : '#9aa6b6',
                textDecoration: on ? 'none' : 'line-through',
              }}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default ColorLegend;
